import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi, type ApiError } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { useToast } from "../lib/toast";
import type { components } from "../lib/api-types";

type ExportCreate = components["schemas"]["ExportCreate"];
type ExportJob = components["schemas"]["ExportJob"];

type ExportAudience = NonNullable<ExportCreate["audience"]>;
type ExportFormat = NonNullable<ExportCreate["format"]>;
type ExportType = NonNullable<ExportCreate["type"]>;

type BlockDetail = {
  code?: string;
  surface?: string;
  trust_gate_status?: string;
  next_actions?: string[];
  evidence_id?: string;
  run_id?: string;
};

type Props = {
  type?: ExportType;
  format?: ExportFormat;
  audience?: string;
  scope?: string[];
  briefingId?: string;
  label?: string;
  compact?: boolean;
  className?: string;
  onCreated?: (job: ExportJob) => void;
};

const allowedAudiences = new Set<ExportAudience>(["ceo", "cfo", "coo", "board", "advisor", "auditor"]);

function normalizeAudience(value?: string): ExportAudience {
  const candidate = (value || "cfo").toLowerCase();
  if (allowedAudiences.has(candidate as ExportAudience)) return candidate as ExportAudience;
  if (candidate === "controller") return "auditor";
  if (candidate === "all") return "board";
  return "cfo";
}

function extractBlockDetail(error: unknown): BlockDetail | null {
  const apiError = error as ApiError | undefined;
  const detail = (apiError?.details as { detail?: unknown } | undefined)?.detail;
  if (!detail || typeof detail !== "object") return null;
  const candidate = detail as BlockDetail;
  return candidate.code === "data_trust_gate_blocked" ? candidate : null;
}

export default function ExecutiveExportButton({
  type = "executive_summary",
  format = "pdf",
  audience = "cfo",
  scope = [],
  briefingId,
  label = "Executive Export",
  compact = false,
  className = "",
  onCreated,
}: Props) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const toast = useToast();
  const qc = useQueryClient();
  const [lastExport, setLastExport] = useState<ExportJob | null>(null);
  const [blocked, setBlocked] = useState<BlockDetail | null>(null);

  const createExport = useMutation({
    mutationFn: () => api.post<ExportJob>("/exports", {
      type,
      format,
      audience: normalizeAudience(audience),
      include_evidence: true,
      include_audit_trail: true,
      scope,
      briefing_id: briefingId,
    } satisfies ExportCreate),
    onSuccess: (job) => {
      setBlocked(null);
      setLastExport(job);
      void qc.invalidateQueries({ queryKey: ["exports", effectiveTenantId] });
      void qc.invalidateQueries({ queryKey: ["agent-runs", effectiveTenantId] });
      void qc.invalidateQueries({ queryKey: ["audit-records", effectiveTenantId] });
      void qc.invalidateQueries({ queryKey: ["evidence-registry", effectiveTenantId] });
      void qc.invalidateQueries({ queryKey: ["audit-integrity-report", effectiveTenantId] });
      if (job.run_id) {
        void qc.invalidateQueries({ queryKey: ["agent-run-evidence-verification", effectiveTenantId, job.run_id] });
      }
      if (briefingId) {
        void qc.invalidateQueries({ queryKey: ["briefings", effectiveTenantId, briefingId] });
        void qc.invalidateQueries({ queryKey: ["briefings", effectiveTenantId, briefingId, "verification"] });
      }
      toast.push({
        kind: "success",
        title: "Export erstellt",
        body: `${job.format.toUpperCase()} · ${job.id} · Evidence ${job.evidence_id ?? "ausstehend"}`,
      });
      onCreated?.(job);
    },
    onError: (error) => {
      const detail = extractBlockDetail(error);
      setBlocked(detail);
      toast.push({
        kind: detail ? "warn" : "error",
        title: detail ? "Export durch Data Trust Gate blockiert" : "Export fehlgeschlagen",
        body: detail?.next_actions?.[0] ?? "Bitte Evidence, Datenquellen und Audit-Status prüfen.",
      });
    },
  });

  return (
    <div className={`executive-export ${compact ? "compact" : ""} ${className}`.trim()}>
      <button
        type="button"
        className={`btn primary ${compact ? "small" : ""}`}
        onClick={() => createExport.mutate()}
        disabled={createExport.isPending}
      >
        {createExport.isPending ? "Export laeuft" : label}
      </button>

      {lastExport && !compact && (
        <div className="export-result success">
          <strong>{lastExport.format.toUpperCase()} bereit</strong>
          <span>{lastExport.id} · Run {lastExport.run_id}</span>
          {lastExport.evidence_id && <code>{lastExport.evidence_id}</code>}
        </div>
      )}

      {blocked && !compact && (
        <div className="export-result blocked">
          <strong>Export gesperrt</strong>
          <span>{blocked.next_actions?.[0] ?? "Quality Gate und Metric Contracts zuerst abschließen."}</span>
          {(blocked.run_id || blocked.evidence_id) && (
            <code>{blocked.run_id ?? "blocked"} · {blocked.evidence_id ?? "no-evidence"}</code>
          )}
        </div>
      )}
    </div>
  );
}
